import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import api from '../../services/api';
import { useCategoryTranslation } from '../../hooks/useCategoryTranslation';
import { IconChevron } from '../../config/meridianIcons';
import type { CategoryBreakdown, SubcategoryBreakdown } from '../../types';

interface CategoryListProps {
  categories: CategoryBreakdown[];
  formatCurrency: (amount: number) => string;
  /** Nombre maximum de lignes affichées. */
  limit?: number;
  /** Période courante — nécessaire pour charger le détail par sous-catégorie. */
  month?: number;
  year?: number;
  weekStart?: string;
  weekEnd?: string;
  isPersonal?: boolean;
}

/**
 * Dépenses par catégorie — liste ordonnée, en remplacement du camembert.
 *
 * Un camembert force l'œil à comparer des angles ; la liste donne le rang, le
 * montant et la part en lecture directe. La colorimétrie est une seule teinte
 * (l'accent) déclinée en opacité décroissante : le rang se lit à l'intensité,
 * sans arc-en-ciel qui entrerait en conflit avec la sémantique crédit/débit.
 *
 * Chaque ligne se DÉPLIE sur ses sous-catégories (même endpoint que l'ancien
 * drill-down du camembert). Le dépliage inline remplace la navigation
 * « aller/retour » : on garde le contexte des autres postes sous les yeux.
 */
const SHADES = [1, 0.74, 0.54, 0.38, 0.26];

export default function CategoryList({
  categories,
  formatCurrency,
  limit = 5,
  month,
  year,
  weekStart,
  weekEnd,
  isPersonal = false,
}: CategoryListProps) {
  const { t } = useTranslation('dashboard');
  const { translateCategoryName } = useCategoryTranslation();
  const [openId, setOpenId] = useState<string | null>(null);
  const [subcategories, setSubcategories] = useState<SubcategoryBreakdown[]>([]);
  const [loading, setLoading] = useState(false);

  // Un changement de période (ou de compte) invalide le détail déjà chargé.
  useEffect(() => {
    setOpenId(null);
    setSubcategories([]);
  }, [categories]);

  const toggle = async (category: CategoryBreakdown) => {
    if (openId === category.categoryId) {
      setOpenId(null);
      return;
    }
    setOpenId(category.categoryId);
    setSubcategories([]);
    setLoading(true);
    try {
      const endpoint = isPersonal
        ? `/settings/personal-category-breakdown/${category.categoryId}`
        : `/settings/category-breakdown/${category.categoryId}`;
      const params = new URLSearchParams();
      if (weekStart && weekEnd) {
        params.append('weekStart', weekStart);
        params.append('weekEnd', weekEnd);
      } else {
        if (month !== undefined) params.append('month', month.toString());
        if (year !== undefined) params.append('year', year.toString());
      }
      const response = await api.get(`${endpoint}?${params.toString()}`);
      setSubcategories(response.data.data ?? []);
    } catch {
      setSubcategories([]);
    } finally {
      setLoading(false);
    }
  };

  if (!categories.length) {
    return <p className="px-5 pb-5 text-sm" style={{ color: 'var(--text-3)' }}>{t('categoryBreakdownDetails.noExpenses')}</p>;
  }

  return (
    <div className="cats">
      {categories.slice(0, limit).map((cat, i) => {
        const shade = SHADES[Math.min(i, SHADES.length - 1)];
        const color = `color-mix(in oklab, var(--pa) ${shade * 100}%, var(--panel-2))`;
        const isOpen = openId === cat.categoryId;
        const name = translateCategoryName(cat.categoryId, cat.categoryName, cat.categoryIsDefault);

        return (
          <div key={cat.categoryId}>
            <button
              type="button"
              onClick={() => toggle(cat)}
              aria-expanded={isOpen}
              className="cat w-full text-left cat-row"
            >
              <div className="c-name">
                <i style={{ background: color }} />
                <span className="truncate">{name}</span>
                <IconChevron size={12} className={clsx('cat-chev', isOpen && 'open')} />
              </div>
              <div className="c-amt num">{formatCurrency(cat.amount)}</div>
              <div className="c-bar">
                <span style={{ width: `${Math.max(0, Math.min(100, cat.percentage))}%`, background: color }} />
              </div>
              <div className="c-pct num">{Math.round(cat.percentage)} %</div>
            </button>

            {isOpen && (
              <div className="cat-sub">
                {loading && <p className="cat-sub-empty">{t('loading', { ns: 'common' })}</p>}
                {!loading && subcategories.length === 0 && (
                  <p className="cat-sub-empty">{t('categoryBreakdownDetails.noSubcategories')}</p>
                )}
                {!loading && subcategories.map((sub) => (
                  <div key={sub.subcategoryId} className="cat-sub-row">
                    <span className="truncate">{translateCategoryName(sub.subcategoryId, sub.subcategoryName)}</span>
                    <span className="num cat-sub-amt">{formatCurrency(sub.amount)}</span>
                    <span className="num cat-sub-pct">{Math.round(sub.percentage)} %</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
